import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const userRef = useRef({});

	useEffect(() => {
		userRef.current = user;
	}, [user]);

	// On mount: attempt to refresh the session using the jrt cookie.
	// Interceptors are already registered at module level in api.js.
	useEffect(() => {
		(async () => {
			const token = localStorage.getItem("token");
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					localStorage.setItem("token", JSON.stringify(data.token));
					// Seta user ANTES de isAuth para que quando TicketsManager montar
					// (triggered pelo isAuth=true), user.queues já esteja disponível.
					setUser(data.user);
					setIsAuth(true);
				} catch {
					// The module-level interceptor already handles 401/403:
					// it clears localStorage and redirects to /login.
				}
			}
			setLoading(false);
		})();
	}, []);

	// Socket connected once; use ref to compare user without reconnecting on every user update
	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === userRef.current.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			history.push("/login");
		} catch (err) {
			if (err?.response?.status !== 401 && err?.response?.status !== 403) {
				toastError(err);
			}
			// On auth failure during logout, still clear state and redirect
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			history.push("/login");
		} finally {
			setLoading(false);
		}
	};

	const updateUser = (partial) => setUser(prev => ({ ...prev, ...partial }));

	return { isAuth, user, loading, handleLogin, handleLogout, updateUser };
};

export default useAuth;
